<template>
  <v-card v-if="lote" flat elevation="3" class="progreso-card mb-4">
    <v-card-title class="progreso-header py-3">
      <v-icon :color="colorEstado" class="mr-2">{{ iconoEstado }}</v-icon>
      <span class="titulo">{{ tituloEstado }}</span>
      <v-spacer />
      <v-btn v-if="enCurso" text small color="error" :disabled="lote.cancelSolicitado" @click="cancelar">
        <v-icon small left>mdi-stop-circle-outline</v-icon>
        {{ lote.cancelSolicitado ? "Cancelando…" : "Cancelar" }}
      </v-btn>
      <v-btn v-else icon small @click="cerrar" title="Ocultar">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pb-2">
      <v-progress-linear
        :value="lote.porcentaje"
        :indeterminate="enCurso && lote.porcentaje === 0"
        :color="colorEstado"
        height="22"
        rounded
        class="mb-3"
      >
        <strong class="white--text">{{ lote.porcentaje }}%</strong>
      </v-progress-linear>

      <div class="contadores">
        <div class="contador">
          <span class="valor">{{ lote.totalOrdenes }}</span>
          <span class="etiqueta">Total</span>
        </div>
        <div class="contador emitidas">
          <span class="valor">{{ lote.totalEmitidas }}</span>
          <span class="etiqueta">Emitidas</span>
        </div>
        <div class="contador errores">
          <span class="valor">{{ lote.totalErrores }}</span>
          <span class="etiqueta">Errores</span>
        </div>
        <div class="contador omitidas">
          <span class="valor">{{ lote.totalOmitidas }}</span>
          <span class="etiqueta">Omitidas</span>
        </div>
        <div class="contador pendientes">
          <span class="valor">{{ lote.totalPendientes }}</span>
          <span class="etiqueta">Pendientes</span>
        </div>
      </div>

      <v-alert v-if="enCurso" dense text type="info" class="mt-3 mb-0 aviso">
        Podés seguir usando el sistema: la emisión continúa aunque cierres esta
        pantalla. Volvé cuando quieras para ver cómo va.
      </v-alert>

      <v-alert v-if="lote.estado === 'INTERRUMPIDO'" dense text type="warning" class="mt-3 mb-0">
        El proceso se interrumpió antes de terminar. Las órdenes que quedaron sin
        emitir se pueden reintentar.
      </v-alert>
    </v-card-text>

    <!-- El detalle solo tiene sentido cuando el lote terminó. -->
    <v-expand-transition>
      <div v-if="!enCurso">
        <v-divider />
        <v-card-text class="pt-3">
          <div class="d-flex align-center mb-3">
            <v-btn-toggle v-model="filtroEstado" dense mandatory class="filtro-resultados">
              <v-btn small value="TODOS">Todas ({{ lote.resultados.length }})</v-btn>
              <v-btn small value="ERROR">Errores ({{ lote.totalErrores }})</v-btn>
              <v-btn small value="OMITIDA">Omitidas ({{ lote.totalOmitidas }})</v-btn>
              <v-btn small value="EMITIDA">Emitidas ({{ lote.totalEmitidas }})</v-btn>
            </v-btn-toggle>
            <v-spacer />
            <v-btn
              v-if="reintentables > 0"
              color="primary" small depressed
              :loading="reintentando"
              @click="reintentar"
            >
              <v-icon small left>mdi-refresh</v-icon>
              Reintentar {{ reintentables }} con error
            </v-btn>
          </div>

          <v-data-table
            :headers="headers"
            :items="resultadosFiltrados"
            :items-per-page="10"
            dense
            class="tabla-resultados"
            no-data-text="No hay ítems con ese estado."
          >
            <template v-slot:item.numeroOrdenVenta="{ item }">
              #{{ item.numeroOrdenVenta }}
            </template>
            <template v-slot:item.total="{ item }">
              {{ moneda(item.total) }}
            </template>
            <template v-slot:item.estado="{ item }">
              <v-chip x-small :color="colorItem(item.estado)" text-color="white">
                {{ item.estado }}
              </v-chip>
            </template>
            <template v-slot:item.motivo="{ item }">
              <span class="motivo">{{ item.motivo || "—" }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import FacturacionProvider from "../../providers/FacturacionProvider";

const INTERVALO_POLLING = 3000;
const ESTADOS_REINTENTABLES = ["ERROR", "CANCELADA", "INTERRUMPIDO"];

export default {
  name: "FacturacionMasivaProgreso",

  data() {
    return {
      lote: null,
      timer: null,
      reintentando: false,
      filtroEstado: "TODOS",
    };
  },

  computed: {
    enCurso() {
      return this.lote?.estado === "PROCESANDO";
    },

    colorEstado() {
      const mapa = {
        PROCESANDO: "primary",
        FINALIZADO: "success",
        FINALIZADO_CON_ERRORES: "warning",
        INTERRUMPIDO: "warning",
        CANCELADO: "grey",
      };
      return mapa[this.lote?.estado] || "primary";
    },

    iconoEstado() {
      const mapa = {
        PROCESANDO: "mdi-progress-clock",
        FINALIZADO: "mdi-check-circle",
        FINALIZADO_CON_ERRORES: "mdi-alert-circle",
        INTERRUMPIDO: "mdi-alert",
        CANCELADO: "mdi-cancel",
      };
      return mapa[this.lote?.estado] || "mdi-progress-clock";
    },

    tituloEstado() {
      if (!this.lote) return "";
      const mapa = {
        PROCESANDO: `Facturando ${this.emitidasSobreTotal}…`,
        FINALIZADO: `Facturación completa: ${this.lote.totalEmitidas} emitidas`,
        FINALIZADO_CON_ERRORES: `Terminó con ${this.lote.totalErrores} errores`,
        INTERRUMPIDO: "Facturación interrumpida",
        CANCELADO: "Facturación cancelada",
      };
      return mapa[this.lote.estado] || this.lote.estado;
    },

    emitidasSobreTotal() {
      const hechas = this.lote.totalOrdenes - this.lote.totalPendientes;
      return `${hechas} de ${this.lote.totalOrdenes}`;
    },

    reintentables() {
      if (!this.lote) return 0;
      return this.lote.resultados.filter((r) => ESTADOS_REINTENTABLES.includes(r.estado)).length;
    },

    resultadosFiltrados() {
      if (!this.lote) return [];
      if (this.filtroEstado === "TODOS") return this.lote.resultados;
      return this.lote.resultados.filter((r) => r.estado === this.filtroEstado);
    },

    headers() {
      return [
        { text: "Orden", value: "numeroOrdenVenta", width: 90 },
        { text: "Cliente", value: "clienteRazonSocial" },
        { text: "Total", value: "total", align: "end", width: 120 },
        { text: "Estado", value: "estado", width: 120 },
        { text: "Detalle", value: "motivo" },
      ];
    },
  },

  mounted() {
    // El estado vive en el backend, no acá: por eso el usuario puede navegar a
    // otra pantalla, volver, o recargar el navegador, y el progreso sigue.
    this.restaurarLoteActivo();
  },

  beforeDestroy() {
    this.detenerPolling();
  },

  methods: {
    async restaurarLoteActivo() {
      try {
        const r = await FacturacionProvider.facturacionMasivaLoteActivo();
        const lote = r.data.facturacionMasivaLoteActivo;
        if (lote) this.seguir(lote);
      } catch (e) {
        // Sin lote activo no hay nada que mostrar.
      }
    },

    /** Engancha el panel a un lote y arranca el polling si sigue en curso. */
    seguir(lote) {
      this.lote = lote;
      this.filtroEstado = lote.totalErrores > 0 ? "ERROR" : "TODOS";
      if (lote.estado === "PROCESANDO") this.iniciarPolling();
      else this.$emit("finalizado", lote);
    },

    iniciarPolling() {
      this.detenerPolling();
      this.timer = setInterval(() => this.refrescar(), INTERVALO_POLLING);
    },

    detenerPolling() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    async refrescar() {
      if (!this.lote) return this.detenerPolling();
      try {
        const r = await FacturacionProvider.facturacionMasivaLote(this.lote.id);
        const lote = r.data.facturacionMasivaLote;
        if (!lote) return this.detenerPolling();

        this.lote = lote;
        if (lote.estado !== "PROCESANDO") {
          this.detenerPolling();
          if (lote.totalErrores > 0) this.filtroEstado = "ERROR";
          this.$emit("finalizado", lote);
        }
      } catch (e) {
        // Un fallo puntual de red no debe cortar el seguimiento: se reintenta
        // en el próximo tick.
      }
    },

    async cancelar() {
      try {
        await FacturacionProvider.facturacionMasivaCancelar(this.lote.id);
        await this.refrescar();
      } catch (e) {
        this.$emit("error", e.message);
      }
    },

    async reintentar() {
      this.reintentando = true;
      try {
        const r = await FacturacionProvider.facturacionMasivaReintentar(this.lote.id);
        const nuevo = r.data.facturacionMasivaReintentar;
        const completo = await FacturacionProvider.facturacionMasivaLote(nuevo.id);
        this.seguir(completo.data.facturacionMasivaLote);
      } catch (e) {
        this.$emit("error", e.message);
      } finally {
        this.reintentando = false;
      }
    },

    cerrar() {
      this.detenerPolling();
      this.lote = null;
    },

    moneda(valor) {
      if (valor == null) return "—";
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(valor);
    },

    colorItem(estado) {
      const mapa = {
        EMITIDA: "success",
        ERROR: "error",
        OMITIDA: "grey",
        CANCELADA: "grey darken-1",
        INTERRUMPIDO: "warning",
        PENDIENTE: "blue-grey lighten-2",
        PROCESANDO: "primary",
      };
      return mapa[estado] || "grey";
    },
  },
};
</script>

<style scoped>
.progreso-card {
  border-radius: 12px !important;
  border-left: 4px solid #667eea;
}

.progreso-header {
  background: #f5f7fa;
}

.titulo {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.contadores {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.contador {
  flex: 1;
  min-width: 90px;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.contador .valor {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.1;
  color: #2c3e50;
}

.contador .etiqueta {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #78909c;
}

.contador.emitidas .valor { color: #2e7d32; }
.contador.errores .valor { color: #c62828; }
.contador.omitidas .valor { color: #757575; }
.contador.pendientes .valor { color: #1565c0; }

.aviso {
  font-size: 0.82rem;
}

.motivo {
  font-size: 0.8rem;
  color: #546e7a;
}

.filtro-resultados .v-btn {
  font-size: 0.72rem;
}
</style>
